<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <BreadCrumbs
          v-model="deviceGroupId"
          :modelId="modelId"
          :serialNo="serialNo"
          @click_breadCrumbs="selectedGroupChange"
          @click_breadCrumbsDevice="selectedDeviceChange"
        />
        <v-divider />
      </v-col>
    </v-row>
    <v-row justify="center" align-content="center">
      <v-col cols="12">
        <v-card outlined>
          <!-- タイトル -->
          <v-card-title v-if="machine">
            <v-row dense>
              <v-col cols="12">
                <custom-icon type="machineInfo" :size="30" :colors="['#162A4F']" class="mr-3"> </custom-icon>
                <span :style="{ display: 'inline-block', height: 100 + '%', 'vertical-align': 'top' }">
                  {{ machine }}
                </span>
              </v-col>
              <v-col cols="12">
                <v-divider></v-divider>
              </v-col>
            </v-row>
          </v-card-title>
          <!-- パラメータ設定 -->
          <v-card-text class="mt-2">
            <v-row v-if="customer">
              <v-col class="ml-5 body-1" cols="4" xl="1" lg="2" md="3" sm="4">
                {{ labels.machine }}
              </v-col>
              <v-col class="body-1" cols="6" xl="2" lg="2" md="4" sm="5">
                {{ customer }}
              </v-col>
            </v-row>
            <v-row class="d-flex align-start mx-3">
              <v-col class="body-1" cols="3" xl="1" lg="2" md="3" sm="4">
                {{ labels.period }}
              </v-col>
              <v-col cols="4" xl="1" lg="2" md="2" sm="3">
                <v-select v-model="selectedItem" :items="items" hide-details outlined dense></v-select>
              </v-col>
              <!-- 開始日カレンダー -->
              <v-col cols="5" xl="2" lg="2" md="3" sm="4" class="ml-1">
                <v-row no-gutters>
                  <v-col cols="12">
                    <Calendar
                      ref="startCal"
                      v-model="startSelectedDate"
                      :type="calendarType"
                      :allowInput="true"
                      :label="selectedItem === 'anyPeriod' ? 'From' : ''"
                    />
                  </v-col>
                  <v-col cols="12" v-if="selectedItem === 'anyPeriod'" class="mt-4">
                    <Calendar
                      ref="endCal"
                      v-model="endSelectedDate"
                      :type="calendarType"
                      :allowInput="true"
                      label="To"
                    />
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12" xl="1" lg="1" md="2" sm="12">
                <v-btn @click="updateChart" color="primary" depressed>
                  <custom-icon type="trendGraph" :size="22" :colors="['#FFFFFF', '#FFFFFF', '#FFFFFF']"> </custom-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
          <!-- グラフ -->
          <v-row class="pa-4">
            <v-col cols="12">
              <Chart ref="airVolumeChart" type="bar" height="250px" />
            </v-col>
            <v-col cols="12" class="pt-0">
              <v-btn color="primary" @click="csvAirClicked" depressed><v-icon>mdi-download</v-icon>CSV</v-btn>
            </v-col>
            <v-col cols="12">
              <Chart ref="powerVolumeChart" type="bar" height="250px" />
            </v-col>
            <v-col cols="12">
              <v-btn color="primary" @click="csvPowerClicked" depressed><v-icon>mdi-download</v-icon>CSV</v-btn>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import CustomIcon from '../components/CustomIcon.vue';
  import Calendar from '../components/Calendar.vue';
  import Chart from '../components/Chart.vue';
  import BreadCrumbs from '@/components/BreadCrumbs.vue';
  import csv from '@/utils/csv';

  export default {
    title() {
      return this.$t(this.$route.meta.title);
    },
    name: 'VolumeGraph',
    components: {
      CustomIcon,
      Calendar,
      Chart,
      BreadCrumbs,
    },
    mixins: [csv],
    data: () => ({
      machine: '',
      customer: '',
      selectedItem: 1,
      modelId: '',
      serialNo: '',
      deviceGroupId: undefined,
      groupId: undefined,
      compressors: undefined,
      sensors: [],
      powerVolumeOptions: {},
      airVolumeOptions: {},
      powerVolumeSeries: [],
      airVolumeSeries: [],
      startSelectedDate: '',
      endSelectedDate: '',
      isSameDay: false,
    }),
    computed: {
      isDevice() {
        // パラメータにデバイスが指定されているか？
        return this.modelId && this.serialNo;
      },
      calendarType() {
        return this.selectedItem === 31 || this.selectedItem === 180 || this.selectedItem === 365 ? 'month' : 'date';
      },
      labels() {
        return {
          machine: this.$t('customerSMachineNo'),
          period: this.$t('displayRange'),
          powerYaxisTitle: `${this.$t('powerConsumption')}[kWh]`,
          airYaxisTitle: `${this.$t('dischargeAirVolume')}[ｍ³]`,
        };
      },
      items() {
        return [
          { text: this.$t('1Day'), value: 1 },
          { text: this.$t('1Week'), value: 7 },
          { text: this.$t('1Month'), value: 31 },
          { text: this.$t('halfYear'), value: 180 },
          { text: this.$t('1Year'), value: 365 },
          {
            text: this.$t('anyPeriodSetting'),
            value: 'anyPeriod',
          },
        ];
      },
      localeId() {
        switch (this.$i18n.locale) {
          case 'en':
            return 1;
          case 'ja':
            return 2;
          case 'zh':
            return 3;
        }
        return 2;
      },
    },
    async created() {
      // デフォルト年月日
      this.startSelectedDate = this.$dayjs().format('YYYY-MM-DD');
      this.endSelectedDate = this.$dayjs().format('YYYY-MM-DD');

      // URLから値を取得
      this.modelId = this.$route.params.model_id;
      this.serialNo = this.$route.params.serial_no;

      this.deviceGroupId = this.$route.params.device_group_id;
      this.groupId = this.$route.params.group_id;
      this.compressors = this.$route.params.compressors;

      if (this.isDevice) {
        this.$store.commit('loading/start_loading');
        try {
          // デバイス情報取得
          const devices = await this.getDevice(this.modelId, this.serialNo);
          if (devices !== null) {
            this.machine = devices[0][`device_name_locale${this.localeId}`];
            this.customer = devices[0].optional_info_2;
            this.deviceGroupId = devices[0].device_group_id;
          }
        } finally {
          this.$store.commit('loading/end_loading');
        }
      }
    },
    async mounted() {
      // デフォルトオプションを取得
      this.powerVolumeOptions = this.$refs.powerVolumeChart.getOptions();
      this.airVolumeOptions = this.$refs.airVolumeChart.getOptions();

      this.airVolumeOptions.title = {
        text: this.$t('dischargeAirVolume'),
        align: 'center',
      };
      this.powerVolumeOptions.title = {
        text: this.$t('powerConsumption'),
        align: 'center',
      };

      // 複数デバイスがある場合はstackで積む
      this.powerVolumeOptions.chart.stacked = true;
      this.airVolumeOptions.chart.stacked = true;

      // y軸設定
      this.powerVolumeOptions.yaxis[0].title.text = this.labels.powerYaxisTitle;
      this.airVolumeOptions.yaxis[0].title.text = this.labels.airYaxisTitle;

      // x軸設定(UTCかどうか)
      this.powerVolumeOptions.xaxis.labels.datetimeUTC = false;
      this.airVolumeOptions.xaxis.labels.datetimeUTC = false;

      // Y軸小数点以下の桁数
      this.powerVolumeOptions.yaxis[0].decimalsInFloat = 0;
      this.airVolumeOptions.yaxis[0].decimalsInFloat = 0;

      // y軸ラベル最小幅（グラフの始まりを合わせるため）
      this.powerVolumeOptions.yaxis[0].labels.minWidth = 120;
      this.airVolumeOptions.yaxis[0].labels.minWidth = 120;

      this.powerVolumeOptions.yaxis[0].labels.formatter = (value) => {
        return this.numberFormat(value);
      };
      this.airVolumeOptions.yaxis[0].labels.formatter = (value) => {
        return this.numberFormat(value);
      };

      // 未設定だとdecimalsInFloat,labels.formatterの影響を受けるので回避
      this.powerVolumeOptions.tooltip.y = {
        formatter: (value) => {
          return this.numberFormat(value);
        },
      };
      this.airVolumeOptions.tooltip.y = {
        formatter: (value) => {
          return this.numberFormat(value);
        },
      };

      // 凡例の色設定
      const colors = [
        '#70B0E0',
        '#FCB715',
        '#2879BD',
        '#12B195',
        '#138372',
        '#AF916D',
        '#C4B17B',
        '#F15629',
        '#6F9EDB',
        '#FEE266',
        '#5DAAFD',
        '#3DABB0',
        '#36B895',
        '#B59087',
        '#B6A887',
        '#F48459',
        '#1D5C95',
        '#BDA851',
        '#4A82BB',
        '#188980',
        '#298A6F',
        '#A67668',
        '#B54C26',
        '#184672',
        '#7E6F37',
        '#32567D',
        '#105C55',
        '#0E2F25',
        '#7B5A4F',
        '#524A34',
        '#793419',
        '#7D3231',
        '#79641A',
        '#313637',
        '#476B75',
        '#7E4C36',
        '#52354C',
        '#0E262F',
        '#544849',
        '#016AB8',
        '#373D49',
        '#FCB15D',
        '#AAF210',
        '#5F646E',
        '#8BA3EB',
        '#FEE266',
        '#A7687A',
        '#3557B8',
        '#DFCFBF',
        '#4A91C5',
        '#5F646E',
        '#C9F459',
        '#7F838A',
        '#A5B8EE',
        '#FCE589',
        '#B68794',
        '#28428A',
        '#A79C8F',
        '#165889',
      ];

      this.powerVolumeOptions.colors = colors;
      this.airVolumeOptions.colors = colors;

      // チャートオプションを更新
      this.$refs.powerVolumeChart.updateOptions(this.powerVolumeOptions);
      this.$refs.airVolumeChart.updateOptions(this.airVolumeOptions);
    },
    methods: {
      async getDevice(modelId, serialNo) {
        const devices = await this.apiRequest('/v1/master-info/userDeviceAuthorities', 'GET', {
          model_id: modelId,
          serial_no: serialNo,
        });
        return devices;
      },
      async getGroups(deviceGroupId, groupId) {
        if (groupId) {
          switch (groupId) {
            case 'all': {
              // 全デバイスを取得
              const allDevices = await this.apiRequest('/v1/master-info/userDeviceAuthorities', 'GET', {
                device_group_id: deviceGroupId,
              });
              return allDevices.map((el) => {
                return {
                  modelId: el.model_id,
                  serialNo: el.serial_no,
                };
              });
            }
            case 'unset': {
              // 全デバイスを取得
              const allDevices = await this.apiRequest('/v1/master-info/userDeviceAuthorities', 'GET', {
                device_group_id: deviceGroupId,
              });

              if (allDevices && allDevices.length > 0) {
                // 普遍的な需要家コードをserialNoから取得
                const consumerId = `${allDevices[0].serial_no.replace(/^.*_/, '')}$`;
                const allGroups = await this.azureRequest('/api/getGroups', 'GET', {
                  deviceGroupId: consumerId,
                });
                // グループに所属しているデバイス
                const groupedDevices = allGroups
                  .map((el) => el.devices)
                  .reduce((a, b) => {
                    return a.concat(b);
                  }, []);

                // グループに所属していないデバイスを抽出
                const nonGrouped = allDevices.filter(
                  (el) =>
                    !groupedDevices.find((d) => {
                      return el.model_id === d.modelId && el.serial_no === d.serialNo;
                    })
                );
                return nonGrouped.map((el) => {
                  return {
                    modelId: el.model_id,
                    serialNo: el.serial_no,
                  };
                });
              }
              break;
            }
            default: {
              const groups = await this.azureRequest('/api/getGroups', 'GET', {
                id: groupId,
              });
              return groups.devices;
            }
          }
        }
        return undefined;
      },
      formatData(data, beginDate, endDate) {
        const result = data.reduce((pre, current) => {
          const key = `${current.modelId}-${current.serialNo}`;
          if (!pre[key]) {
            pre[key] = {
              modelId: current.modelId,
              serialNo: current.serialNo,
              power: [],
              air: [],
            };
          }

          // 表示用にに整形
          const power = current.sensors.find((el) => el.label === '消費電力量');
          const air = current.sensors.find((el) => el.label === '吐出空気量');
          const measureDate = current.measureDate;

          const powerData = {
            x: this.$dayjs(measureDate).toISOString(true),
            y: power.data,
          };

          const airData = {
            x: this.$dayjs(measureDate).toISOString(true),
            y: air.data,
          };

          pre[key].power.push(powerData);
          pre[key].air.push(airData);

          return pre;
        }, []);

        // Y軸の項目
        const mesureDateArray = [];
        let currentDate = this.$dayjs(beginDate);
        while (currentDate.isBefore(endDate)) {
          mesureDateArray.push(this.$dayjs(currentDate).toISOString(true));
          currentDate = currentDate.add(1, this.selectedItem === 1 || this.isSameDay ? 'hours' : 'days');
        }

        // センサデータがない項目を作成する関数
        const fillData = (list) => {
          const noData = mesureDateArray
            .filter((key) => !list.find((el) => el.x === key))
            .map((el) => {
              return { x: el, y: null };
            });
          let result = list;
          if (noData.length > 0) {
            result = noData.concat(result);
          }
          // 昇順で並べなおし
          result.sort((a, b) => (a.x > b.x ? 1 : -1));
          return result;
        };

        for (const deviceData of Object.values(result)) {
          // 歯抜けデータがあるとstackの描画がおかしくなるのでダミーを追加
          deviceData.power = fillData(deviceData.power);
          deviceData.air = fillData(deviceData.air);
        }

        return Object.values(result);
      },
      async volumeApiData(beginDate, endDate) {
        console.log(
          `開始日：${beginDate.format('YYYY-MM-DD HH:mm:ss.sss')} 終了日:${endDate.format('YYYY-MM-DD HH:mm:ss.sss')}`
        );
        let data = undefined;

        const devicesParam = this.isDevice
          ? [
              {
                modelId: this.modelId,
                serialNo: this.serialNo,
              },
            ]
          : this.compressors.map((el) => {
              return {
                modelId: el.modelId,
                serialNo: el.serialNo,
              };
            });

        const grouped = this.selectedItem === 1 || this.isSameDay ? 'hour' : 'day';
        // 基準日の判定はバッチに合わせてUTC
        let statusId;

        const baseDate = this.$dayjs().utc();
        const today = this.$dayjs().startOf('day');

        if (baseDate.isSameOrBefore(endDate) || this.selectedItem === 1 || this.isSameDay) {
          let from = this.$dayjs(today);
          let to = this.$dayjs(baseDate);
          if (this.selectedItem === 1 || this.isSameDay) {
            // 日単位はタイムゾーンの補正が必要(トレンドグラフに合わせる)
            // タイムゾーンが設定されていないことを考慮して前後12時間分を含めて取得
            from = this.$dayjs(beginDate).add(-12, 'hour');
            to = this.$dayjs(endDate).add(12, 'hour');
          }

          // 時間別・本日分はバッチで取得していないので取得をリクエスト
          const reserved = await this.azureRequest('/api/monthlyReportReserve', 'POST', {
            batchStart: true,
            reserve: {
              once: true,
              devices: devicesParam,
              fromDate: from.startOf('day').format('YYYY-MM-DD'),
              toDate: to.endOf('day').format('YYYY-MM-DD'),
              reserveStartDate: this.$dayjs().utc().add(-1, 'day').format('YYYY-MM-DD HH:mm:ss.SSSSSS0'),
              grouped: [
                {
                  type: grouped,
                  monthlyReport: false,
                  powerAir: true,
                  verify: false,
                },
              ],
            },
            ttl: 60 * 10,
          });
          statusId = reserved.statusId;
        }

        console.log(`バッチ予約:${statusId}`);

        let measureDateFrom = beginDate.startOf('day');
        let measureDateTo = endDate.endOf('day');

        // 日単位はタイムゾーンの補正が必要(トレンドグラフに合わせる)
        if (this.selectedItem === 1 || this.isSameDay) {
          // タイムゾーンが設定されていないことを考慮して前後12時間分を含めて取得
          measureDateFrom = this.$dayjs(measureDateFrom).add(-12, 'hour');
          measureDateTo = this.$dayjs(measureDateTo).add(12, 'hour');
        }

        const dateFrom = grouped === 'day' ? measureDateFrom.add(-1, 'day') : measureDateFrom;
        data = await this.azureRequest('/api/getMonthlyReport', 'POST', {
          statusId: statusId,
          devices: devicesParam,
          measureDateFrom: dateFrom.format('YYYY-MM-DD HH:mm:ss.SSS'),
          measureDateTo: measureDateTo.format('YYYY-MM-DD HH:mm:ss.SSS'),
          grouped: grouped,
        });

        // 日単位はタイムゾーンの補正が必要(トレンドグラフに合わせる)
        if (this.selectedItem === 1 || this.isSameDay) {
          let timeZoneChache = await Promise.all(
            devicesParam.map(async (el) => {
              const devices = await this.getDevice(el.modelId, el.serialNo);
              el.timezone = devices[0].time_zone;
              return el;
            })
          );

          data = data
            .map((el) => {
              // 確認用にtimezoneを取得
              let timezone = timeZoneChache.find(
                (chachEl) => chachEl.modelId === el.modelId && chachEl.serialNo === el.serialNo
              );

              if (timezone.timezone == null) {
                // timezoneが存在しない場合は、ブラウザのtimezoneで補正
                const utcOffset = this.$dayjs().utcOffset();
                let tmp = this.$dayjs.utc(el.measureDate).utcOffset(utcOffset).format('YYYY-MM-DD HH:mm:ss'); //2022-08-30 00:00:00
                //console.log(el.measureDate + "→" + tmp)
                el.measureDate = tmp;
              }

              return el;
            })
            .filter((el) => {
              let tmp = this.$dayjs(el.measureDate);

              return tmp.isSameOrAfter(beginDate) && tmp.isSameOrBefore(endDate);
            });
        }

        // グラフ用にデータを整形
        const result = this.formatData(data, beginDate, endDate);

        return result;
      },
      async updateChart() {
        if (!this.isDateSettingValid()) {
          return;
        }

        this.$store.commit('loading/start_loading');
        try {
          // デフォルトオプションを取得
          this.powerVolumeOptions = this.$refs.powerVolumeChart.getOptions();
          this.airVolumeOptions = this.$refs.airVolumeChart.getOptions();

          const baseDate =
            this.selectedItem === 31 || this.selectedItem === 180 || this.selectedItem === 365
              ? this.$dayjs(this.startSelectedDate).endOf('month').format('YYYY-MM-DD')
              : this.startSelectedDate;
          console.log(baseDate);

          // UTCで開始・終了日時を設定
          let beginDate = this.$dayjs(baseDate).startOf('day');
          let endDate = this.$dayjs(baseDate).endOf('day');
          switch (this.selectedItem) {
            case 1:
              //beginDate = this.$dayjs(baseDate)
              break;
            case 7:
              beginDate = beginDate.add(-6, 'days').startOf('day');
              break;
            case 31:
              beginDate = beginDate.startOf('month').startOf('day');
              break;
            case 180:
              beginDate = beginDate.add(-6, 'month').startOf('day');
              break;
            case 365:
              beginDate = beginDate.subtract(1, 'year').startOf('day');
              break;
            case 'anyPeriod':
              endDate = this.$dayjs(this.endSelectedDate).endOf('day');
              break;
          }
          // 開始と終了が同日だった場合は、1日を同じように処理する
          this.isSameDay = beginDate.isSame(endDate, 'day');

          // タイムゾーンに合わせた描画範囲を設定
          for (const options of [this.airVolumeOptions, this.powerVolumeOptions]) {
            if (this.selectedItem === 1 || this.selectedItem === undefined || this.isSameDay) {
              options.xaxis.min = beginDate.valueOf();
              options.xaxis.max = endDate.valueOf();
            } else {
              options.xaxis.min = this.$dayjs(beginDate).startOf('day').add(-1, 'second').valueOf();
              options.xaxis.max = this.$dayjs(endDate).startOf('day').valueOf();
            }
          }

          // 0:00だと日付が表示されるので回避
          const dayFormatt = this.selectedItem === 1 || this.isSameDay ? 'HH:mm' : 'MM/dd';
          this.powerVolumeOptions.xaxis.labels.format = dayFormatt;
          this.airVolumeOptions.xaxis.labels.format = dayFormatt;

          // チャートオプションを更新
          this.$refs.powerVolumeChart.updateOptions(this.powerVolumeOptions);
          this.$refs.airVolumeChart.updateOptions(this.airVolumeOptions);

          const volumeData = await this.volumeApiData(beginDate, endDate);
          await this.updateChartData(volumeData);
        } finally {
          this.$store.commit('loading/end_loading');
        }
      },
      async updateChartData(volumeData) {
        const powerVolumeSeries = [];
        const airVolumeSeries = [];
        for (const el of volumeData) {
          const devices = await this.getDevice(el.modelId, el.serialNo);
          const tmp = devices[0].optional_info_2 ? ` ${devices[0].optional_info_2}` : '';
          const label =
            devices && devices.length > 0 ? `${devices[0][`device_name_locale${this.localeId}`]}${tmp}` : '';

          powerVolumeSeries.push({
            name: label,
            data: el.power,
          });

          airVolumeSeries.push({
            name: label,
            data: el.air,
          });
        }

        this.powerVolumeOptions = this.$refs.powerVolumeChart.getOptions();
        this.airVolumeOptions = this.$refs.airVolumeChart.getOptions();

        // データがある場合のみ凡例を表示
        this.powerVolumeOptions.legend.show = powerVolumeSeries.length > 1;
        this.airVolumeOptions.legend.show = airVolumeSeries.length > 1;

        this.$refs.powerVolumeChart.updateOptions(this.powerVolumeOptions);
        this.$refs.airVolumeChart.updateOptions(this.airVolumeOptions);

        this.powerVolumeSeries = powerVolumeSeries;
        this.airVolumeSeries = airVolumeSeries;

        // チャートを描画
        await Promise.all([
          this.$refs.powerVolumeChart.updateSeries(powerVolumeSeries),
          this.$refs.airVolumeChart.updateSeries(airVolumeSeries),
        ]);
      },

      // ぱんくずリストクリック
      selectedGroupChange(group) {
        this.$router.push({
          name: group.children.length > 0 ? 'HomeAdmin' : 'HomeConsumer',
          params: { device_group_id: group.device_group_id },
        });
      },
      selectedDeviceChange(device) {
        this.$router.push({
          name: 'CompressorInfo',
          params: { model_id: device.model_id, serial_no: device.serial_no },
        });
      },
      isDateSettingValid() {
        let errMsg = '';
        if (this.selectedItem === 'anyPeriod') {
          // 任意期間設定の場合

          if (this.$refs.startCal.hasError || this.$refs.endCal.hasError) {
            errMsg = 'invalidDateEntered';
          } else {
            const beginDate = this.$dayjs(this.startSelectedDate);
            const endDate = this.$dayjs(this.endSelectedDate);

            // 開始・終了が逆の場合
            if (endDate.isBefore(beginDate)) {
              errMsg = 'toMustBeALaterDateThanFrom';
            }

            // 指定期間が366日を超えている場合
            if (endDate.diff(beginDate, 'day') > 366) {
              errMsg = 'theOptionalPeriodMustBeWithin366Days';
            }
          }
        } else {
          if (this.$refs.startCal.hasError) {
            errMsg = 'invalidDateEntered';
          }
        }

        if (errMsg) {
          this.$store.commit('snackbar/show_snackbar', {
            text: this.$t(errMsg),
            color: 'error',
            timeout: 5000,
            multiline: false,
          });
          return false;
        } else {
          return true;
        }
      },
      csvPowerClicked() {
        this._csvClicked(this.powerVolumeSeries);
      },
      csvAirClicked() {
        this._csvClicked(this.airVolumeSeries);
      },
      _csvClicked(series) {
        console.log('CSVダウンロード');

        let items = [];
        let header = [this.$t('dateTime')];
        const keys = series.map((el) => el.name);
        header = header.concat(keys);
        items.push(header);

        // CSV用に整形
        const formatted = [];
        series.forEach((el) => {
          el.data.forEach((elData) => {
            const dateTime = elData.x;
            const value = elData.y;

            if (!formatted[dateTime]) {
              formatted[dateTime] = {
                dateTime: dateTime,
                data: [],
              };
            }
            formatted[dateTime].data.push({
              name: el.name,
              value: value,
            });
          });
        });
        formatted.sort((a, b) => (a.dateTime < b.dateTime ? 1 : -1));

        Object.values(formatted).forEach((el) => {
          let lineData = [];

          const dateTimeLabel = this.$dayjs(el.dateTime).format(
            this.selectedItem === 1 || this.isSameDay ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD'
          );
          lineData.push(dateTimeLabel);

          const values = keys.map((key) => {
            const tmp = el.data.find((tmp) => tmp.name === key);
            return tmp ? tmp.value : undefined;
          });
          lineData = lineData.concat(values);

          items.push(lineData);
        });

        const fileName = `${this.$dayjs().format('YYYYMMDD')}.csv`;
        this.downloadCsv(fileName, items);
      },
      numberFormat: (value) => {
        if (isNaN(value) || value === undefined) {
          return '';
        }
        let formatter = new Intl.NumberFormat('ja-JP');
        return formatter.format(value);
      },
    },
  };
</script>
